import { useState, useEffect, useRef } from "react";
import { AlertTriangle, ChevronRight, Droplets, Waypoints, ShieldCheck, FileText, Loader2, Sparkles, SendHorizontal, Gauge, Siren, Clock3, Download, FlaskConical, CheckCircle2, Copy, RotateCcw } from "lucide-react";
import { postAliaNarrative, postAliaAsk, downloadReport } from "../lib/api";
import { useT, useLang, timeAgo } from "../lib/i18n";

const SEV = {
  critical: { ring: "border-l-red-500", bg: "bg-red-950/20", dot: "bg-red-500", text: "text-red-400", key: "sev_critical" },
  warning: { ring: "border-l-amber-500", bg: "bg-amber-950/20", dot: "bg-amber-500", text: "text-amber-400", key: "sev_warning" },
  info: { ring: "border-l-emerald-500", bg: "bg-emerald-950/20", dot: "bg-emerald-500", text: "text-emerald-400", key: "sev_info" },
  nodata: { ring: "border-l-slate-600", bg: "bg-slate-900/30", dot: "bg-slate-500", text: "text-slate-400", key: "sev_nodata" },
};

const cleanLine = (s) =>
  s
    .replace(/\*\*/g, "")
    .replace(/^\s*#{1,6}\s*/, "")
    .replace(/^\s*[-•]\s+/, "")
    .replace(/\s+$/, "")
    .trim();

// Renders ALIA prose cleanly: emphasised title, bold "Label:" fields, highlighted
// confidence footer, plain paragraphs otherwise. Backend text is left untouched.
const AliaProse = ({ text }) => {
  const lines = (text || "")
    .replace(/\r/g, "")
    .split("\n")
    .map(cleanLine)
    .filter(Boolean);
  return (
    <div className="space-y-1.5 text-[11px] leading-relaxed text-slate-200" data-testid="alia-prose">
      {lines.map((ln, i) => {
        const isTitle = i === 0 && /(INFORME|ALIA RISK REPORT)/i.test(ln) && !ln.includes(":");
        const isConf = /^(Confianza|Confidence)\s*:/i.test(ln);
        const kv = ln.match(/^([^:]{2,42}):\s*(.+)$/);
        if (isTitle)
          return (
            <div key={i} className="text-[11px] font-mono font-semibold tracking-wide text-indigo-300 uppercase">
              {ln}
            </div>
          );
        if (isConf)
          return (
            <div key={i} className="mt-1.5 pt-1.5 border-t border-indigo-500/20 text-[10px] font-mono text-emerald-300">
              {ln}
            </div>
          );
        if (kv)
          return (
            <p key={i}>
              <span className="font-semibold text-slate-100">{kv[1]}:</span> <span className="text-slate-300">{kv[2]}</span>
            </p>
          );
        return <p key={i} className="text-slate-300">{ln}</p>;
      })}
    </div>
  );
};

const AlertCard = ({ a, onFocus }) => {
  const t = useT();
  const lang = useLang();
  const s = SEV[a.severity];
  const isForecast = a.kind === "forecast";
  const [expanded, setExpanded] = useState(false);
  const [narrative, setNarrative] = useState(null);
  const [loading, setLoading] = useState(false);
  const title = lang === "en" && a.titulo_en ? a.titulo_en : a.titulo;

  useEffect(() => {
    setNarrative(null);
    setExpanded(false);
  }, [lang]);

  const toggleReport = async (e) => {
    e.stopPropagation();
    const next = !expanded;
    setExpanded(next);
    if (next && !narrative && !loading) {
      setLoading(true);
      try {
        const res = await postAliaNarrative(a.id, lang);
        setNarrative(res);
      } catch {
        setNarrative({ narrative: t.alia_err_narrative, sources: [] });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onFocus(a)}
      onKeyDown={(e) => e.key === "Enter" && onFocus(a)}
      data-testid="alert-card-item"
      className={`w-full text-left border border-slate-800 border-l-4 ${s.ring} ${s.bg} rounded-md p-3 space-y-2 hover:border-slate-700 transition-colors duration-150 cursor-pointer`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono font-semibold tracking-wider ${s.text}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${s.dot} animate-pulse`} />
          {t[s.key]}
        </span>
        <span className="font-mono text-[10px] text-slate-500">{a.id}</span>
      </div>
      {a.simulation && (
        <div
          data-testid="simulation-badge"
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-[9px] font-mono text-amber-300 tracking-wider"
        >
          <FlaskConical className="h-3 w-3" /> {t.sim_badge}{a.checkpoint ? ` · ${a.checkpoint}` : ""}
        </div>
      )}
      {isForecast && (
        <div
          data-testid="forecast-badge"
          className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-violet-500/15 border border-violet-500/40"
        >
          <span className="inline-flex items-center gap-1 text-[9px] font-mono text-violet-300 tracking-wider" title={t.tip_lead}>
            <Clock3 className="h-3 w-3" /> {t.forecast_badge} · {t.lead_label} ≤{a.lead_time_h} h
          </span>
          {a.probability_pct != null && (
            <span className="text-[9px] font-mono text-violet-300">P {a.probability_pct}%</span>
          )}
        </div>
      )}
      {isForecast && a.sources_agree && (
        <div
          data-testid="sources-agree-badge"
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-[9px] font-mono text-emerald-300 tracking-wider"
          title={t.tip_sources_agree}
        >
          <CheckCircle2 className="h-3 w-3" /> {t.sources_agree}
        </div>
      )}
      {a.aemet_confirmed && (
        <div
          data-testid="aemet-confirmed-badge"
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-teal-500/15 border border-teal-500/40 text-[9px] font-mono text-teal-300 tracking-wider"
          title={t.tip_aemet_confirmed}
        >
          <CheckCircle2 className="h-3 w-3" /> {t.badge_aemet}{a.aemet_station ? ` · ${a.aemet_station}` : ""}
        </div>
      )}
      {a.sovereign_verified && (
        <div
          data-testid="sovereign-verified-badge"
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-fuchsia-500/15 border border-fuchsia-500/40 text-[9px] font-mono text-fuchsia-300 tracking-wider"
        >
          <ShieldCheck className="h-3 w-3" /> {t.sovereign_badge}
        </div>
      )}
      {(a.saih_ground_truth || a.ems_confirmed) && (
        <div className="flex flex-wrap gap-1">
          {a.saih_ground_truth && (
            <span data-testid="saih-ground-truth-badge" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-500/15 border border-sky-500/40 text-[9px] font-mono text-sky-300 tracking-wider">
              <Gauge className="h-3 w-3" /> {t.badge_saih} · {a.saih_gauge}
            </span>
          )}
          {a.ems_confirmed && (
            <span data-testid="ems-confirmed-badge" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-500/15 border border-orange-500/40 text-[9px] font-mono text-orange-300 tracking-wider">
              <Siren className="h-3 w-3" /> {t.badge_ems} · {a.ems_code}
            </span>
          )}
        </div>
      )}
      {a.saih_reading && a.saih_thresholds && (
        <div data-testid="saih-threshold-line" className="text-[10px] font-mono text-sky-300/90">
          <Gauge className="inline h-3 w-3 mr-1" />
          {a.saih_reading.gauge}: {a.saih_reading.flow_m3s} m³/s ({a.saih_reading.at}) · {a.saih_reading.status.toUpperCase()} · {a.saih_reading.return_period}
          <span className="text-slate-500"> · CHJ {a.saih_thresholds.low}/{a.saih_thresholds.medium}/{a.saih_thresholds.high} · T10 {a.saih_thresholds.qt10} · T100 {a.saih_thresholds.qt100} · T500 {a.saih_thresholds.qt500} m³/s</span>
          {a.saih_reading.note && (
            <div data-testid="saih-reading-note" className="mt-0.5 text-amber-300/90 font-sans">
              {a.saih_reading.note[lang] || a.saih_reading.note.es}
            </div>
          )}
        </div>
      )}
      <div className="text-[13px] font-medium text-slate-100 leading-snug">{title}</div>
      <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500">
        <span className="text-slate-400">{a.municipio}</span>
        <span>·</span>
        <span>{a.ccaa}</span>
        <span>·</span>
        <span>{isForecast ? `${t.eta_label} ≤${a.lead_time_h} h` : timeAgo(a.timestamp, lang)}</span>
      </div>
      {typeof a.variance_pct === "number" && (
        <div className="flex items-center gap-2 text-[10px] font-mono" data-testid="alert-telemetry">
          <span
            className={`px-1.5 py-0.5 rounded ${
              a.variance_pct >= 400
                ? "bg-red-500/15 text-red-400"
                : a.variance_pct >= 200
                ? "bg-amber-500/15 text-amber-400"
                : "bg-slate-700/40 text-slate-400"
            }`}
            title={t.tip_variance}
          >
            Δ {a.variance_pct > 0 ? "+" : ""}{a.variance_pct}% {t.vs_base}
          </span>
          <span className="px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400" title={t.tip_overlap}>
            SNCZI {a.snczi_overlap}%
          </span>
          {isForecast && a.proj_peak_mmh != null && (
            <span className="px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-300" title={t.proj_peak}>
              {a.proj_peak_mmh} mm/h
            </span>
          )}
          {typeof a.confidence === "number" && (
            <span className="px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300">{t.confidence} {a.confidence}%</span>
          )}
        </div>
      )}
      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-3 font-mono text-[10px] text-slate-400">
          <span className="flex items-center gap-1" title={t.tip_risk}>
            <AlertTriangle className="h-3 w-3" /> {a.risk_score}
          </span>
          <span className="flex items-center gap-1" title={t.tip_depth}>
            <Droplets className="h-3 w-3" /> {a.inundation_depth_m} m
          </span>
          <span className="flex items-center gap-1" title={t.tip_infra}>
            <Waypoints className="h-3 w-3" /> {a.infra_affected}
          </span>
        </div>
        <span data-testid="alert-action-focus-button" className="inline-flex items-center gap-0.5 text-[10px] font-mono text-cyan-400">
          {t.locate} <ChevronRight className="h-3 w-3" />
        </span>
      </div>

      <button
        onClick={toggleReport}
        data-testid="alia-report-button"
        className="w-full mt-1 inline-flex items-center justify-center gap-1.5 rounded border border-indigo-500/40 bg-indigo-500/10 px-2 py-1.5 text-[10px] font-mono tracking-wider text-indigo-300 hover:bg-indigo-500/20 transition-colors"
      >
        <Sparkles className="h-3 w-3" />
        {expanded ? t.alia_report_hide : t.alia_report}
      </button>

      {(a.severity === "critical" || a.severity === "warning") && a.report_reference && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); downloadReport(a.report_reference, lang); }}
          data-testid="download-pdf-button"
          className="w-full inline-flex items-center justify-center gap-1.5 rounded border border-cyan-500/40 bg-cyan-500/10 px-2 py-1.5 text-[10px] font-mono tracking-wider text-cyan-300 hover:bg-cyan-500/20 transition-colors"
        >
          <Download className="h-3 w-3" /> {t.download_pdf}
        </button>
      )}

      {expanded && (
        <div data-testid="alia-narrative" className="mt-1 rounded border border-indigo-500/25 bg-[#0B0F19] p-2.5">
          <div className="flex items-center gap-1.5 mb-1.5 text-[9px] font-mono tracking-wider text-indigo-300">
            <FileText className="h-3 w-3" /> {t.alia_report_header}
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-[11px] text-slate-400 font-mono py-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t.drafting}
            </div>
          ) : (
            <>
              <AliaProse text={narrative?.narrative} />
              {narrative?.sources?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {narrative.sources.map((src) => (
                    <span key={src} className="px-1.5 py-0.5 rounded bg-slate-800 text-[8px] font-mono text-slate-400">
                      {src}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const newSessionId = () => `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const AliaConsole = () => {
  const t = useT();
  const lang = useLang();
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const sessionId = useRef(newSessionId());
  const endRef = useRef(null);

  useEffect(() => setMessages([]), [lang]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading]);

  const send = async (preset) => {
    const q = (preset ?? question).trim();
    if (!q || loading) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: q }]);
    setQuestion("");
    setLoading(true);
    try {
      const res = await postAliaAsk(q, lang, history, sessionId.current);
      setMessages((m) => [...m, { role: "assistant", content: res.answer, sources: res.sources || [] }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: t.alia_err_ask, sources: [] }]);
    } finally {
      setLoading(false);
    }
  };

  const copy = (text, idx) => {
    navigator.clipboard?.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 1500);
  };

  const reset = () => {
    setMessages([]);
    sessionId.current = newSessionId();
  };

  return (
    <div className="mt-auto shrink-0 flex flex-col border-t-2 border-indigo-500/50 bg-gradient-to-b from-[#0C1020] to-[#070A12] shadow-[0_-10px_30px_rgba(0,0,0,0.55)]" style={{ maxHeight: "48%" }} data-testid="alia-console">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-indigo-500/15">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center h-7 w-7 rounded-md bg-indigo-500/20 border border-indigo-500/40 shrink-0">
            <Sparkles className="h-3.5 w-3.5 text-indigo-300" />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-xs font-semibold tracking-wide text-indigo-200">{t.console_header}</span>
            <span className="text-[8px] font-mono tracking-wider text-slate-500 uppercase">{t.console_sub}</span>
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={reset}
            data-testid="alia-chat-clear"
            className="flex items-center gap-1 text-[9px] font-mono text-slate-500 hover:text-slate-300 transition-colors"
          >
            <RotateCcw className="h-3 w-3" /> {t.chat_clear}
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2.5 space-y-2.5 resilio-scroll min-h-[130px]" data-testid="alia-transcript">
        {messages.length === 0 && !loading && (
          <div className="space-y-1.5" data-testid="alia-starters">
            <p className="text-[10px] text-slate-500 font-mono">{t.chat_empty_hint}</p>
            {t.chat_starters.map((s, i) => (
              <button
                key={i}
                onClick={() => send(s)}
                data-testid={`alia-starter-${i}`}
                className="block w-full text-left text-[11px] text-slate-300 bg-[#111726] border border-slate-700/70 rounded px-2.5 py-1.5 hover:border-indigo-500/60 hover:text-slate-100 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {messages.map((m, i) =>
          m.role === "user" ? (
            <div key={i} className="flex justify-end" data-testid="alia-msg-user">
              <div className="max-w-[85%] rounded-lg rounded-br-sm bg-indigo-500/15 border border-indigo-500/25 px-2.5 py-1.5 text-[11px] text-slate-100 whitespace-pre-line">
                {m.content}
              </div>
            </div>
          ) : (
            <div key={i} className="flex flex-col items-start gap-1" data-testid="alia-msg-assistant">
              <div className="max-w-[92%] rounded-lg rounded-bl-sm bg-[#111726] border border-slate-700/70 px-2.5 py-2">
                <AliaProse text={m.content} />
                {m.sources?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {m.sources.map((src) => (
                      <span key={src} className="px-1.5 py-0.5 rounded bg-slate-800 text-[8px] font-mono text-cyan-400">
                        {src}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => copy(m.content, i)}
                data-testid={`alia-copy-${i}`}
                className="flex items-center gap-1 text-[9px] font-mono text-slate-500 hover:text-slate-300 transition-colors ml-1"
              >
                <Copy className="h-2.5 w-2.5" /> {copiedIdx === i ? t.chat_copied : t.chat_copy}
              </button>
            </div>
          )
        )}

        {loading && (
          <div className="flex items-center gap-2 text-[11px] text-slate-400 font-mono" data-testid="alia-loading">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t.analysing}
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="flex items-center gap-1.5 p-3 border-t border-slate-800/60">
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          data-testid="alia-ask-input"
          placeholder={t.ask_placeholder}
          className="flex-1 bg-[#111726] border border-slate-700 rounded px-2.5 py-2 text-[11px] text-slate-200 font-mono placeholder:text-slate-600 focus:outline-none focus:border-indigo-500"
        />
        <button
          onClick={() => send()}
          data-testid="alia-ask-send"
          disabled={loading || !question.trim()}
          className="grid place-items-center h-8 w-8 rounded bg-indigo-500/20 border border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/30 disabled:opacity-50 transition-colors"
        >
          <SendHorizontal className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export const AlertFeed = ({ alerts, summary, onFocus }) => {
  const t = useT();
  const [filter, setFilter] = useState("all");
  const forecastCount = alerts.filter((a) => a.kind === "forecast").length;
  const critCount = alerts.filter((a) => a.kind !== "forecast" && a.severity === "critical").length;
  const warnCount = alerts.filter((a) => a.kind !== "forecast" && a.severity === "warning").length;
  const filtered = alerts.filter((a) => {
    if (filter === "all") return true;
    if (filter === "forecast") return a.kind === "forecast";
    return a.kind !== "forecast" && a.severity === filter;
  });

  const Tab = ({ id, label, testid }) => (
    <button
      onClick={() => setFilter(id)}
      data-testid={testid}
      className={`flex-1 px-2 py-1.5 text-[10px] font-mono tracking-wider rounded transition-colors ${
        filter === id ? "bg-slate-800 text-slate-100" : "text-slate-500 hover:text-slate-300"
      }`}
    >
      {label}
    </button>
  );

  return (
    <aside className="w-96 min-w-[360px] shrink-0 bg-[#0B0F19] border-l border-slate-800 flex flex-col" data-testid="alert-feed-panel">
      <div className="p-3 border-b border-slate-800">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs font-mono font-semibold tracking-wider uppercase text-slate-300">{t.feed_title}</h2>
          <span className="font-mono text-[10px] text-slate-500 tabular-nums">{alerts.length}</span>
        </div>
        <div className="flex gap-1 bg-[#111726] rounded p-1">
          <Tab id="all" label={`${t.tab_all} (${alerts.length})`} testid="alert-feed-filter-all" />
          <Tab id="critical" label={`${t.tab_crit} (${critCount})`} testid="alert-feed-filter-critical" />
          <Tab id="warning" label={`${t.tab_warn} (${warnCount})`} testid="alert-feed-filter-warning" />
          <Tab id="forecast" label={`${t.tab_forecast} (${forecastCount})`} testid="alert-feed-filter-forecast" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2.5 resilio-scroll">
        {filtered.length === 0 ? (
          <div className="text-center text-slate-600 font-mono text-xs py-8">{t.no_anomalies}</div>
        ) : (
          filtered.map((a) => <AlertCard key={a.id} a={a} onFocus={onFocus} />)
        )}
      </div>
      <AliaConsole />
    </aside>
  );
};
